import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import type { NodeInstanceDataPort } from '@glueapp/graph-types';
import { OutPortTypeAnchor, ShowMorePortsOutPort } from '../nodeComposition/Ports';
import { GraphModal, GraphModalAnchor } from './GraphModal';
import { NodeEventInsulator } from './NodeInterfaceEventInsulator';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
`;

const MorePortsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: stretch;

    overflow-y: scroll;
    min-width: 150px;
    max-height: 50vh;

    ::-webkit-scrollbar {
        width: 11px;
        height: 11px;
    }
    ::-webkit-scrollbar-button {
        width: 0;
        height: 0;
    }
    ::-webkit-scrollbar-thumb {
        background: ${props => props.theme.backgroundColor};
        border: 1px solid ${props => props.theme.subtleAccentColor};
        border-radius: 50px;
    }
    ::-webkit-scrollbar-thumb:hover {
        background: ${props => props.theme.subtleAccentColor};
    }
    ::-webkit-scrollbar-thumb:active {
        background: ${props => props.theme.subtleAccentColor};
    }
    ::-webkit-scrollbar-track {
        background: ${props => props.theme.backgroundColor};
        border: 0 none #ffffff;
        border-radius: 53px;
    }
    ::-webkit-scrollbar-track:hover {
        background: ${props => props.theme.elevatedBackgroundColor};
    }
    ::-webkit-scrollbar-track:active {
        background: ${props => props.theme.elevatedBackgroundColor};
    }
    ::-webkit-scrollbar-corner {
        background: transparent;
    }
`;

const MorePortsListItem = styled.li`
    text-align: left;
    padding: 4px 8px;
    cursor: pointer;
    color: ${props => props.theme.textColor};
    &:hover {
        background-color: ${props => props.theme.elevatedBackgroundColor};
    }
`;

export const SearchInput = styled.input`
    display: block;
    color: ${props => props.theme.textColor};
    background: ${props => props.theme.backgroundColor};
    border: 1px solid rgb(60, 60, 60);
    height: 24px;
    padding: 4px;
`;

interface Props {
    ports: NodeInstanceDataPort[];
    onPortClicked: (port: NodeInstanceDataPort) => void;
}

export const MorePorts: React.FC<Props> = ({ ports, onPortClicked }) => {
    const [showModal, setShowModal] = useState(false);
    const [search, setSearch] = useState('');
    const visiblePorts = ports.filter(port => search === '' || port.name.includes(search));
    const searchInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        setTimeout(() => {
            if (showModal) {
                searchInputRef.current?.focus();
            }
        }, 30);
    }, [showModal]);
    return (
        <>
            <ShowMorePortsOutPort onPointerDown={() => setShowModal(true)} />
            {showModal ? (
                <NodeEventInsulator>
                    <GraphModalAnchor>
                        <OutPortTypeAnchor>
                            <GraphModal
                                onDismiss={() => {
                                    setShowModal(false);
                                    setSearch('');
                                }}
                            >
                                <Container>
                                    <SearchInput
                                        autoFocus
                                        ref={searchInputRef}
                                        placeholder={'Search'}
                                        value={search}
                                        onChange={event => setSearch(event.target.value)}
                                    />
                                    {visiblePorts.length ? (
                                        <MorePortsList>
                                            {visiblePorts.map((port, index) => (
                                                <MorePortsListItem
                                                    key={port.name}
                                                    onPointerDown={event => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        onPortClicked(port);
                                                    }}
                                                >
                                                    {port.name}
                                                </MorePortsListItem>
                                            ))}
                                        </MorePortsList>
                                    ) : null}
                                </Container>
                            </GraphModal>
                        </OutPortTypeAnchor>
                    </GraphModalAnchor>
                </NodeEventInsulator>
            ) : null}
        </>
    );
};
